import { TOGGLE_FEATURE } from "../types/constants";
import {
  CarrierType,
  ItemType,
  OrderStatus,
  ShippingType,
} from "../types/enums";
import {
  BankHoliday,
  Pickup,
  Product,
  Shipping,
  ShippingContent,
} from "../types/interfaces";
import { getDayOfWeekEn, getDayOfWeekFr } from "./date.utils";

const FedexDeliveryStatusCode = {
  AD: "En cours de livraison",
  AF: "Dans un centre FedEx local",
  AO: "Livraison prévue à l’heure",
  AP: "En attente de ramassage",
  AR: "Arrivé dans un centre FedEx",
  CA: "Expédition annulée",
  CH: "Lieu modifié",
  DD: "Retard de livraison",
  DE: "Exception de livraison",
  DL: "Livré",
  DP: "Départ effectué",
  DY: "Retard",
  ED: "En route pour la livraison",
  EP: "En route pour le ramassage",
  FD: "Au centre de destination FedEx",
  HL: "Conservé au centre",
  HP: "Prêt pour le ramassage par le destinataire",
  IT: "En transit",
  LO: "Quitté le point d'origine",
  OC: "Commande créée",
  OD: "En cours de livraison",
  OF: "Dans un centre d'origine FedEx",
  PD: "Retard de ramassage",
  PU: "Ramassé",
  RS: "Retour à l'expéditeur",
  SE: "Exception d’expédition",
};

export const isIntegratedCarrier = (
  carrier: CarrierType,
  shippingType: ShippingType,
  connectedUserEmail?: string
) => {
  switch (shippingType) {
    case ShippingType.PICKUP:
      return TOGGLE_FEATURE.INTEGRATED_PICKUP_CARRIERS(
        connectedUserEmail
      ).includes(carrier);
    case ShippingType.DELIVERY:
      return TOGGLE_FEATURE.INTEGRATED_DELIVERY_CARRIERS(
        connectedUserEmail
      ).includes(carrier);
    default:
      return false;
  }
};

export const isPickupRequestAllowedForDoctor = (
  products: Product[],
  isRedo: boolean = false
): boolean => {
  const allowedProducts: ItemType[] = isRedo
    ? [
        ItemType.IMPLANT,
        ItemType.BRIDGE_IMPLANT,
        ItemType.IMPLANT_CROWN,
        ItemType.SPLINTED_CROWN_IMPLANT,
        ItemType.BRIDGE_PONTIC,
        ItemType.PARTIAL_DENTURE,
        ItemType.FULL_DENTURE,
        ItemType.FULL_DENTURE_WAX_BITE,
        ItemType.PARTIAL_DENTURE_HOOK,
      ]
    : [
        ItemType.FULL_DENTURE,
        ItemType.FULL_DENTURE_WAX_BITE,
        ItemType.CUSTOM_TRAY,
        ItemType.PARTIAL_DENTURE,
        ItemType.PARTIAL_DENTURE_HOOK,
        ItemType.ADDING_TEETH_ON_EXISTING_DENTURE,
        ItemType.DENTURE_REPAIR,
      ];

  let allItems: ItemType[] = [];
  (products ?? []).forEach(product => {
    allItems = [
      ...allItems,
      ...(product?.anatomyItem ?? []).map(item => item.itemType),
    ];
    allItems = [
      ...allItems,
      ...(product?.removableItem ?? []).map(item => item.itemType),
    ];
    allItems = [
      ...allItems,
      ...(product?.implantItem ?? []).map(item => item.itemType),
    ];
  });

  return !!allItems.find(item => allowedProducts.includes(item));
};

export const isBusinessDay = (
  date: Date,
  bankHolidays: BankHoliday[]
): boolean => {
  /*
   * Saturday and Sunday are not business days
   */
  if (
    date.getDay() === 6 ||
    date.getDay() === 0 ||
    isBankHoliday(date, bankHolidays)
  )
    return false;
  return true;
};

export const addMoreDays = (date: Date, numberOfDaysToAdd: number): Date => {
  const dt = new Date(date);
  if (numberOfDaysToAdd <= 0) {
    return dt;
  }
  dt.setDate(dt.getDate() + numberOfDaysToAdd);
  return dt;
};

export const isBankHoliday = (date: Date, bankHolidays: BankHoliday[]) => {
  const isBankHoliday = bankHolidays.find(bankHoliday => {
    return (
      !bankHoliday.useCustomDelay &&
      date.getTime() >= bankHoliday.beginDate &&
      date.getTime() <= bankHoliday.endDate
    );
  });
  return !!isBankHoliday;
};

export const getNextBusinessDay = (
  date: Date,
  bankHolidays: BankHoliday[] = []
): { date: Date; displayDayEn: string; displayDayFr: string } => {
  let dayCountBeforeNextBusinessDay = 1;
  let dt = addMoreDays(date, 1);
  while (!isBusinessDay(dt, bankHolidays)) {
    dayCountBeforeNextBusinessDay++;
    dt = addMoreDays(dt, 1);
  }
  const isTomorrow = dayCountBeforeNextBusinessDay === 1;
  return {
    date: dt,
    displayDayEn: isTomorrow ? "Tomorrow" : getDayOfWeekEn(dt.getDay()),
    displayDayFr: isTomorrow ? "Demain" : getDayOfWeekFr(dt.getDay()),
  };
};

export const translateFedexDeliveryStatusCode = (
  deliveryStatusCode: string,
  deliveryCarrier?: CarrierType
): string | null => {
  switch (deliveryCarrier) {
    case CarrierType.FEDEX_TNT:
      return FedexDeliveryStatusCode[deliveryStatusCode] || null;
  }
  return null;
};

export const hasDeliveryTrackingNumber = (
  shippings: ShippingContent[]
): boolean => {
  const deliveryShippingWithTrackingNumber = (shippings ?? []).find(
    shippingContent =>
      shippingContent.shipping.trackingNumber &&
      shippingContent.shipping.shippingType === ShippingType.DELIVERY
  );
  return deliveryShippingWithTrackingNumber ? true : false;
};

export const hasPickupTrackingNumber = (
  shippingContents: ShippingContent[],
  pickups: Pickup[] = []
): boolean => {
  const hasUnintagratedPickupTrackingNumber = pickups.find(
    pickup => pickup?.shipping?.trackingNumber
  );
  const pickupShippingWithTrackingNumber = (shippingContents ?? []).find(
    shippingContent => {
      return (
        shippingContent.shipping.trackingNumber &&
        shippingContent.shipping.shippingType === ShippingType.PICKUP
      );
    }
  );
  return (
    !!pickupShippingWithTrackingNumber || !!hasUnintagratedPickupTrackingNumber
  );
};

export const displayDeliveryStatusButton = (
  deliveryCarrier: CarrierType,
  orderStatus: OrderStatus,
  hasDeliveryTrackingNumber: boolean
): boolean => {
  return (
    deliveryCarrier === CarrierType.FEDEX_TNT &&
    hasDeliveryTrackingNumber &&
    (orderStatus === OrderStatus.SHIPPED ||
      orderStatus === OrderStatus.DELIVERED)
  );
};

export const getDeliveryTrackingUrl = (
  deliveryCarrier: CarrierType | undefined,
  trackingNumber: string | null | undefined
): string | null => {
  const normalizedTrackingNumber = trackingNumber?.trim();
  if (!normalizedTrackingNumber) {
    return null;
  }
  switch (deliveryCarrier) {
    case CarrierType.CIBLEX:
      return `https://secure.extranet.ciblex.fr/extranet/client/corps.php?module=colis&colis=${encodeURIComponent(
        normalizedTrackingNumber
      )}`;
    default:
      return null;
  }
};

export const getLastTrackingNumber = (
  shippings: (ShippingContent & { shipping: Shipping })[]
) => {
  const deliveryShippings = (shippings ?? []).filter(shipping => {
    return shipping.shipping.shippingType === ShippingType.DELIVERY;
  });
  if (!deliveryShippings || deliveryShippings.length === 0) {
    return null;
  }
  return [...deliveryShippings].sort(
    (a: ShippingContent, b: ShippingContent) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )[0].shipping.trackingNumber;
};
